import { Nullable }		from "ts-base/nullable";

import { Refresh }		from "@v3/preact/refresh";

import * as i18n		from "@geotoura/shared/i18n";
import * as upModel		from "@geotoura/shared/upModel";
import * as commonModel	from "@geotoura/shared/commonModel";

import * as url				from "@geotoura/common/util/url";
import * as Server			from "@geotoura/common/Server";
import * as tracking		from "@geotoura/common/privacy/tracking";
import { cookieWarning }	from "@geotoura/common/privacy/cookieWarning";

import { UpData, buildUpData }	from "@geotoura/up/upData";

import {
	MapView,
	Model,
	ScreenSize,
	PersonalSchema,
	FlightSchema,
	ExampleRouteSchema,
} from "@geotoura/up/model";

import * as actions		from "@geotoura/up/actions";
import * as helper		from "@geotoura/up/initHelpers";
import * as util		from "@geotoura/up/util";
import {
	destinationState,
	routeVersion,
}	from "@geotoura/up/constants";
import { Messages }		from "@geotoura/up/locale";
import { App }			from "@geotoura/up/App";

/* ========================================================================= */
// common functions
/* ========================================================================= */

const init = (upData:UpData, languageCode:i18n.LanguageCode):void => {
	cookieWarning(languageCode);

	const screenSize:ScreenSize = helper.currentScreenSize();

	const savedRoute		= helper.loadSavedRoute(upData, routeVersion);
	const savedMapViewData	= Nullable.then(savedRoute)(it => helper.getSavedRouteMapViewData(upData, it));

	// TODO repr use AsUrlParameter - we have to merge our id concept with the representation stuff
	const urlParams	= url.rawParameters();

	const regionId			= Nullable.then(urlParams.get("region"))(commonModel.RegionId.fromString);
	const region			= Nullable.then(regionId)(upData.region.getOrNull);

	const territoryId		= Nullable.then(urlParams.get("territory"))(commonModel.TerritoryId.fromString);
	const territory			= Nullable.then(territoryId)(upData.territory.getOrNull);

	const exampleRouteId	= Nullable.then(urlParams.get("exampleRouteId"))(commonModel.ExampleRouteId.fromString);
	const exampleRoute		= Nullable.then(exampleRouteId)(upData.route.getOrNull);

	// NOTE empty destinations should not happen in practice
	const firstDestinationId		= exampleRoute?.destinations?.[0] ?? null;
	const firstDestination			= Nullable.then(firstDestinationId)(upData.destination.getOrNull);
	const firstDestinationRegionId	= firstDestination !== null ? firstDestination.r : null;
	const firstDestinationRegion	= Nullable.then(firstDestinationRegionId)(upData.region.getOrNull);

	// called with an example route, but there is one in the local storage: ask the user which one to use
	const modalOverwriteRoute:boolean =
		savedRoute !== null		&&
		savedRoute.length > 0	&&
		firstDestination !== null;

	const mapDefaults:MapView	=
		modalOverwriteRoute													?	helper.defaultMapView															:
		savedMapViewData	!== null										?	helper.destinationMapView(savedMapViewData.region,	savedMapViewData.marker)	:
		territory			!== null										?	helper.territoryMapView(territory)												:
		region				!== null										?	helper.regionMapView(region)													:
		firstDestination	!== null && firstDestinationRegion	!== null	?	helper.destinationMapView(firstDestinationRegion,	firstDestination)			:
		helper.defaultMapView;

	// display RegionSelection when no start parameter is given
	const viewRegions	=
		savedRoute			=== null	&&
		territory			=== null	&&
		region				=== null	&&
		firstDestination	=== null;

	// viewRegions enables the howto as a second step - without viewRegions, we try to display it here
	const modalHowto	=
		!viewRegions					&&
		savedRoute			=== null	&&
		firstDestination	=== null;

	const howtoRegionIds	=		territory	!== null ? upData.rawRegions.filter((r) => r.territoryId === territory.id).map((r) => r.id)
								:	region		!== null ? [ region.id ]
								:	[];

	const msg	= Messages.of[languageCode];

	const initial:Model	= {
		screenSize:		screenSize,

		// TODO up this needs some love. or at least an explanation.
		route:			{
			items:	savedRoute !== null && !modalOverwriteRoute		? savedRoute	: [],
			index:	-1,
			added:	savedRoute !== null && !modalOverwriteRoute,
		},
		savedRoute:		savedRoute !== null && modalOverwriteRoute	? savedRoute	: [],

		viewRegions:			viewRegions,

		modalHowto:				modalHowto,
		modalHowtoRegionIds:	howtoRegionIds,
		modalHelp:				false,
		modalOverwriteRoute:	modalOverwriteRoute,
		modalRouteDelete:		false,
		modalRequest:			false,

		destination:			null,
		drawerState:			{
			open:		false,
			transition:	false,
			content:	"destination",
		},

		destinationState:		destinationState,

		mapDefaults:		mapDefaults,
		setMapViewEvent:	null,

		// NOTE this _can_ contain invalid ids on purpose so the error is logged in the database
		// TODO up should we prevent this by using another type of id?
		entryPoint: {
			route:			exampleRouteId,
			region:			regionId,
			territory:		territoryId,
		},

		warnings:		[],
		exampleRouteData:	ExampleRouteSchema.initial(),
		flightEnabled:		true,
		personalData:
			PersonalSchema.injectPartial({
				duration:	savedRoute !== null && !modalOverwriteRoute ? helper.calcDuration(savedRoute) : 14,
				nrAdults:	2,
				nrBabies:	0,
				nrKids:		0,
				roomSingle:	0,
				roomDouble:	1,
				kidBeds:	0,
				babyBeds:	0,
			})(
				PersonalSchema.initial()
			),
		flightData:			FlightSchema.initial(),

		requestResponse:	null,
		requestEnroute:		false,
	};

	const container	= Nullable.unsafeGet(document.querySelector<Element>("#up-container"));

	const refresh:Refresh<Model> =
		Refresh.install({
			container:	container,
			initial:	initial,
			component:	model => <App upData={upData} languageCode={languageCode} messages={msg} model={model}/>,
		});
	actions.setRefresh(refresh);

	// GMap uses useEffect to set global.map and this happens after initialization
	window.setTimeout(() => {
		if (exampleRouteId !== null && firstDestinationId !== null && !modalOverwriteRoute) {
			void actions.loadRoute(upData, languageCode, exampleRouteId, firstDestinationId);
		}
		else if (savedRoute !== null && !modalOverwriteRoute) {
			void actions.selectDestination(upData, languageCode, savedRoute[0].id, 0, true);
		}
	}, 100);

	window.addEventListener(
		"resize",
		() => actions.setScreensize(helper.currentScreenSize()),
		false
	);

	// needs timeout because google analytics is added after pageload by tag manager
	window.setTimeout(() => {
		tracking.gaSendEvent({
			action:		"planner-load",
			category:	"planner",
			label:		util.labelEntryPoint(initial.entryPoint),
			value:		2,
		});
	}, 1000);
};

document.onreadystatechange = ():void => {
	if (document.readyState === "complete") {
		const languageCode:i18n.LanguageCode	=
			i18n.LanguageCode.valueOrDefault(document.documentElement.getAttribute("lang"));

		// TODO data display an error when this goes wrong
		void Server.getData({ lang: languageCode })
		.then((json:upModel.RawUpData) => {
			init(buildUpData(json), languageCode);
		});
	}
};
